import { openInPanel } from "./p9-workspace.js";
import { connection } from "./p9-live-page.js";
import { principalsInput } from "../editor/toolbar-ui.js";
import { hexCentres, snapToGrid, squareLines, type Grid } from "./map-grid.js";
import { dragHandle, handles, middle, moveArea, outline, sizeText, type AreaShape, type Point } from "./map-shapes.js";

type PageCard = { id: string; title: string; url: string; excerpt: string };
type Marker = {
  id: string;
  label: string;
  ownLabel: string;
  body: string;
  bodyHtml: string;
  x: number;
  y: number;
  icon: string;
  color: string;
  layerId: string | null;
  page: PageCard | null;
  pageLocked: boolean;
  section: string | null;
  sectionTitle: string | null;
  restrictedTo: string;
  noAi: boolean;
  gone?: boolean;
};
type Area = AreaShape & {
  id: string;
  label: string;
  ownLabel: string;
  body: string;
  bodyHtml: string;
  color: string;
  layerId: string | null;
  page: PageCard | null;
  pageLocked: boolean;
  section: string | null;
  sectionTitle: string | null;
  restrictedTo: string;
  noAi: boolean;
  gone?: boolean;
};
type CustomIcon = { value: string; name: string; url: string; onPage: string | null; canChange: boolean };
type Layer = { id: string; name: string; sort: number; restrictedTo: string; noAi: boolean };
type MapImage = {
  state: "ready" | "preparing" | "failed" | "missing" | "notshared";
  name: string | null;
  attachmentId: string | null;
  width: number;
  height: number;
  tileSize: number;
  maxLevel: number;
  tiles: string | null;
};
type MapData = {
  id: string;
  title: string;
  pageId: string;
  canEdit: boolean;
  image: MapImage;
  grid: Grid & { scale: string };
  layers: Layer[];
  markers: Marker[];
  areas: Area[];
  icons: string[];
  customIcons: CustomIcon[];
  /** Where a new icon can go (this map's page and those above it that the person may change; "global" in admin mode). */
  iconPlaces: Array<{ value: string; label: string }> | null;
  colors: string[];
  gridKinds: string[];
};

const SVG_NS = "http://www.w3.org/2000/svg";
const SHAPE_LABELS: Record<string, string> = { circle: "Circle", rectangle: "Rectangle", cone: "Cone", freeform: "Freeform" };
const SHAPE_HINTS: Record<string, string> = {
  circle: "Press at the middle and drag out the radius (Esc to cancel).",
  rectangle: "Drag from one corner to the other (Esc to cancel).",
  cone: "Press at the cone's tip and drag towards where it points (Esc to cancel).",
  freeform: "Click to place corners; click the first corner again, or press Enter, to close the shape (Esc to cancel).",
};
const EMPTY_ID = "00000000-0000-0000-0000-000000000000";
const GRID_LABELS: Record<string, string> = { none: "None", square: "Squares", "hex-flat": "Hexes, flat top", "hex-pointy": "Hexes, pointy top" };
const ICON_LABELS: Record<string, string> = {
  pin: "Pin", city: "City", town: "Town", castle: "Castle", house: "House", tavern: "Tavern", temple: "Temple", tower: "Tower", cave: "Cave",
  mountain: "Mountain", forest: "Forest", port: "Port", camp: "Camp", danger: "Danger", star: "Star", flag: "Flag", question: "Unknown",
};
// Glyphs on a 10 × 10 grid, drawn in the marker's head.
const GLYPHS: Record<string, string> = {
  pin: '<circle cx="5" cy="5" r="2.4" class="fill"/>',
  city: '<path d="M1 9V4h2.5v5M3.5 9V1h3v8M6.5 9V5H9v4"/>',
  town: '<path d="M1 9V5l2.5-2.5L6 5v4M6 9V6.5L7.5 5 9 6.5V9"/>',
  castle: '<path d="M1 9V2h1.6v1.5h1.6V2h1.6v1.5h1.6V2H9v7zM4 9V6.5h2V9"/>',
  house: '<path d="M1 5l4-4 4 4M2 4.5V9h6V4.5"/>',
  tavern: '<path d="M2 2h5v7H2zM7 3.5h1.5v3H7"/>',
  temple: '<path d="M.5 3.5 5 1l4.5 2.5zM2 4.5v4M4 4.5v4M6 4.5v4M8 4.5v4M1 9h8"/>',
  tower: '<path d="M3 9.5V2h4v7.5M2.5 2V.6h1.4V2M4.3 2V.6h1.4V2M6.1 2V.6h1.4V2"/>',
  cave: '<path d="M1 9C1 4 3 1.5 5 1.5S9 4 9 9M3.5 9c0-2 .7-3.5 1.5-3.5S6.5 7 6.5 9"/>',
  mountain: '<path d="M.5 9 4 3l2 3.2L7 4.5 9.5 9z"/>',
  forest: '<path d="M5 .8 1.6 5.8h6.8zM5 3.3 2 8.3h6zM5 8.3v1.4"/>',
  port: '<path d="M5 2.2v7.3M3 4h4M1.5 6.5C2 8.5 3.5 9.5 5 9.5s3-1 3.5-3"/><circle cx="5" cy="1.3" r=".9"/>',
  camp: '<path d="M.5 9 5 1.5 9.5 9zM5 9V5.5L3.5 9"/>',
  danger: '<path d="M5 1.3v5M5 8.2v.5"/>',
  star: '<path class="fill" d="m5 .5 1.3 3 3.2.2-2.5 2.1.8 3.2L5 7.3 2.2 9l.8-3.2L.5 3.7l3.2-.2z"/>',
  flag: '<path d="M2 9.5V.5M2 1h6L6.5 3 8 5H2"/>',
  question: '<path d="M3.2 3.2a1.8 1.8 0 1 1 2.6 1.6c-.6.4-.8.8-.8 1.6M5 8.2v.5"/>',
};

/**
 * A map (docs/tapestry-design.md §3.7): an image shown in tiles, with an optional grid, markers and marked areas
 * (circles, rectangles, cones, freeform shapes) on layers.
 *
 * View mode (on pages): pan and zoom, layers on and off, a marker's or area's details. Edit mode (in the page's edit
 * view): add markers by clicking and areas by drawing, drag and reshape them, edit them; the map's name, image and grid;
 * layers; custom icons. Every change is saved at once, one thing at a time, and others who have the page open get it
 * through the live connection.
 *
 * Attributes: data-url (the map's endpoint), data-base (the archive's root), data-mode ("edit" or "view"),
 * data-orphan (no longer in the page's text), data-marker (open this marker or area), data-focus-page (open the marker
 * of this page), data-place-page and data-place-title (place this page: "Place on a map"), data-tall (fill most of the
 * window).
 */
export class P9Map extends HTMLElement {
  #url = "";
  #base = "";
  #map: MapData | null = null;
  #editing = false;
  #viewport!: HTMLElement;
  #world!: HTMLElement;
  #tiles!: HTMLElement;
  #backdrop!: HTMLElement;
  #grid!: SVGSVGElement;
  #markers!: HTMLElement;
  #areas!: SVGSVGElement;
  #areaMenu!: HTMLDetailsElement;
  #panel!: HTMLFormElement;
  #status!: HTMLElement;
  #title!: HTMLElement;
  #editTools!: HTMLElement;
  #layerMenu!: HTMLDetailsElement;
  #placeButton!: HTMLButtonElement;
  #snapButton!: HTMLButtonElement;
  #editToggle!: HTMLButtonElement;
  #view = { x: 0, y: 0, scale: 1 };
  #hiddenLayers = new Set<string>();
  #selected: string | null = null;
  #placing = false;
  /** The page being placed ("Place on a map"), until it's placed. */
  #placePage: { id: string; title: string; section: string } | null = null;
  /** An area being drawn: its shape and, in image pixels, where the drag started, where the pointer is, a freeform shape's corners. */
  #drawing: { shape: string; start?: Point; cursor?: Point; corners: Point[] } | null = null;
  #snap = false;
  #frame = 0;
  #fitted = false;
  #tileLevel = -1;
  #reloadTimer = 0;
  #onRecord = (kind: string, id: string): void => {
    if (kind !== "map" || id !== this.#map?.id) return;
    // Several changes in a row (someone dragging markers): one reload.
    window.clearTimeout(this.#reloadTimer);
    this.#reloadTimer = window.setTimeout(() => void this.#load(), 400);
  };

  connectedCallback(): void {
    this.#url = this.dataset.url ?? "";
    this.#base = this.dataset.base ?? document.body.dataset.base ?? "/";
    this.classList.add("map");
    this.classList.toggle("is-tall", this.dataset.tall === "true");
    this.replaceChildren();
    this.#build();
    void this.#load();
    listeners.add(this.#onRecord);
    void listen();
    new ResizeObserver(() => this.#schedule()).observe(this.#viewport);
  }

  disconnectedCallback(): void {
    listeners.delete(this.#onRecord);
  }

  // ---- Data ----

  async #load(): Promise<void> {
    try {
      const response = await fetch(this.#url, { headers: { Accept: "application/json" } });
      if (!response.ok) {
        this.#say(response.status === 404 ? "This map is gone, or you can't see it." : "Couldn't load the map.");
        return;
      }
      const previous = this.#map;
      this.#map = (await response.json()) as MapData;
      if (previous?.image.tiles !== this.#map.image.tiles) this.#tileLevel = -1;
    } catch {
      this.#say("Couldn't load the map.");
      return;
    }
    const map = this.#map;
    this.#title.textContent = map.title;
    // The box takes the image's shape (as tall as the window allows), so there are no empty bands around it.
    if (map.image.width > 0) this.#viewport.style.aspectRatio = `${map.image.width} / ${map.image.height}`;
    if (!this.#fitted) this.#setEditing(this.dataset.mode === "edit" && map.canEdit);
    else this.#setEditing(this.#editing && map.canEdit);
    this.#renderLayerMenu();
    if (!this.#fitted) {
      this.#fitted = true;
      this.#fit();
      const open = map.markers.find((m) => m.id === this.dataset.marker || (this.dataset.focusPage && m.page?.id === this.dataset.focusPage));
      const openArea = map.areas.find((a) => a.id === this.dataset.marker || (this.dataset.focusPage && a.page?.id === this.dataset.focusPage));
      if (open) this.#focusOn(open, () => this.#click(open));
      else if (openArea) {
        const [mx, my] = middle(openArea, map.image.width, map.image.height);
        this.#focusOn({ x: mx / map.image.width, y: my / map.image.height }, () => this.#clickArea(openArea));
      }
      if (this.dataset.placePage && map.canEdit)
        this.#startPlacingPage(this.dataset.placePage, this.dataset.placeTitle ?? "this page", this.dataset.placeSection ?? "");
    }
    this.#imageNote();
    this.#render();
    // Tiles are being cut: look again in a while.
    if (map.image.state === "preparing") window.setTimeout(() => void this.#load(), 4000);
  }

  async #call<T>(handler: string, query: Record<string, string>, body: unknown): Promise<T | null> {
    const params = new URLSearchParams({ handler, ...query });
    try {
      const response = await fetch(`${this.#url}?${params}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", RequestVerificationToken: token() },
        body: JSON.stringify(body ?? {}),
      });
      const result = (await response.json().catch(() => ({}))) as T & { error?: string };
      if (!response.ok) {
        this.#say(result.error ?? "That didn't work.");
        return null;
      }
      this.#say("Saved");
      return result;
    } catch {
      this.#say("Couldn't save; check your connection.");
      return null;
    }
  }

  #marker(id: string): Marker | undefined {
    return this.#map?.markers.find((m) => m.id === id);
  }

  #area(id: string): Area | undefined {
    return this.#map?.areas.find((a) => a.id === id);
  }

  #replaceArea(area: Area): void {
    if (!this.#map) return;
    const others = this.#map.areas.filter((a) => a.id !== area.id);
    this.#map.areas = area.gone ? others : [...others, area];
    this.#render();
  }

  #replace(marker: Marker): void {
    if (!this.#map) return;
    const others = this.#map.markers.filter((m) => m.id !== marker.id);
    this.#map.markers = marker.gone ? others : [...others, marker];
    this.#render();
  }

  // ---- Building ----

  #build(): void {
    const bar = div("board-toolbar");
    this.#title = document.createElement("strong");
    this.#title.className = "board-title";
    this.#editTools = div("board-tools");
    this.#editTools.hidden = true;
    this.#placeButton = button("+ Marker", "Then click the map where it goes", () => this.#togglePlacing());
    this.#snapButton = button("Snap", "Put new and moved markers in the middle of a grid cell", () => {
      this.#snap = !this.#snap;
      this.#snapButton.setAttribute("aria-pressed", String(this.#snap));
    });
    this.#snapButton.setAttribute("aria-pressed", "false");
    this.#areaMenu = document.createElement("details");
    this.#areaMenu.className = "dropdown map-layer-menu";
    const areaSummary = document.createElement("summary");
    areaSummary.textContent = "+ Area ▾";
    areaSummary.title = "Mark an area: a circle, rectangle, cone or freeform shape";
    const areaList = div("dropdown-list map-layer-list");
    for (const shape of Object.keys(SHAPE_LABELS)) areaList.append(button(SHAPE_LABELS[shape]!, SHAPE_HINTS[shape]!, () => this.#startDrawing(shape)));
    this.#areaMenu.append(areaSummary, areaList);
    const deleteMap = button("Delete map", "This map is no longer in the page's text; delete it for good", () => void this.#deleteMap());
    deleteMap.className = "danger";
    deleteMap.hidden = this.dataset.orphan !== "true";
    this.#editTools.append(
      this.#placeButton,
      this.#areaMenu,
      this.#snapButton,
      button("Map…", "The map's name, image and grid", () => this.#openSettings()),
      button("Layers…", "Add, rename and restrict layers", () => this.#openLayers()),
      button("Icons…", "Your own marker icons, for this map and others", () => this.#openIcons()),
      deleteMap,
    );
    this.#layerMenu = document.createElement("details");
    this.#layerMenu.className = "dropdown map-layer-menu";
    this.#layerMenu.hidden = true;
    const viewTools = div("board-tools");
    viewTools.append(
      button("−", "Zoom out", () => this.#zoomBy(1 / 1.4)),
      button("+", "Zoom in", () => this.#zoomBy(1.4)),
      button("Fit", "Show the whole map", () => this.#fit()),
      this.#layerMenu,
      button("⛶", "Full screen", () => this.#toggleFull()),
    );
    // Where it can be switched to editing (on its own, e.g. in the side panel), for those who may change it.
    this.#editToggle = button("✎ Edit", "Edit this map here: markers, areas, layers, icons", () => this.#setEditing(!this.#editing));
    this.#editToggle.hidden = true;
    viewTools.append(this.#editToggle);
    // Popped out while editing, it opens in the side panel ready to edit, next to the page's text.
    if (window.name !== "panel")
      viewTools.append(button("⇥ Side panel", "Pin this map next to the wiki while you browse (and keep editing it there)",
        () => openInPanel(`${this.#base}maps/${this.#mapId()}/open${this.#editing ? "?edit=true" : ""}`)));
    bar.append(this.#title, this.#editTools, viewTools);

    this.#viewport = div("board-viewport map-viewport");
    this.#viewport.tabIndex = 0;
    this.#viewport.setAttribute("aria-label", "Map: drag to pan, Ctrl + scroll or pinch to zoom, arrow keys to move");
    this.#world = div("map-world");
    this.#backdrop = div("map-tiles map-backdrop");
    this.#tiles = div("map-tiles");
    this.#world.append(this.#backdrop, this.#tiles);
    this.#grid = document.createElementNS(SVG_NS, "svg");
    this.#grid.classList.add("map-grid");
    this.#areas = document.createElementNS(SVG_NS, "svg");
    this.#areas.classList.add("map-areas");
    this.#markers = div("map-markers");
    this.#viewport.append(this.#world, this.#grid, this.#areas, this.#markers);
    this.#panel = document.createElement("form");
    this.#panel.className = "board-panel";
    this.#panel.hidden = true;
    this.#status = document.createElement("p");
    this.#status.className = "board-status muted small";
    this.#status.setAttribute("aria-live", "polite");
    const stage = div("board-stage");
    stage.append(this.#viewport, this.#panel);
    this.append(bar, stage, this.#status);
    this.#wire();
  }

  // The person's handle (for "Only me"): given to the map where it stands alone, else the page editor's.
  #me(): string | undefined {
    return this.dataset.me ?? document.querySelector<HTMLElement>("p9-editor[data-me]")?.dataset.me;
  }

  #mapId(): string {
    return this.#map?.id ?? this.#url.split("/").pop() ?? "";
  }

  #setEditing(on: boolean): void {
    if (this.#editing !== on) this.#closePanel();
    this.#editing = on;
    this.#editTools.hidden = !on;
    this.#editToggle.hidden = this.dataset.toggleEdit !== "true" || !this.#map?.canEdit;
    this.#editToggle.textContent = on ? "Done editing" : "✎ Edit";
    this.#editToggle.setAttribute("aria-pressed", String(on));
    this.classList.toggle("is-editing", on);
    const map = this.#map;
    this.#snapButton.hidden = !map || map.grid.kind === "none";
  }

  // The map's image, when it can't be shown: why, in plain words.
  #imageNote(): void {
    const map = this.#map!;
    const messages: Record<string, string> = {
      preparing: "Preparing the map… (big images take a moment)",
      failed: "This map's image couldn't be read. Pick another one under Map….",
      missing: map.canEdit ? "This map has no image. Pick one under Map…." : "This map has no image yet.",
      notshared: map.canEdit
        ? "This map's image is in the trash, on another page, or not shared with everyone who sees this page, so it isn't shown. Pick another under Map…, or change the image's sharing."
        : "This map's image isn't shared with you.",
    };
    let note = this.#viewport.querySelector<HTMLElement>(".map-note");
    if (map.image.state === "ready") return note?.remove();
    if (!note) {
      note = div("map-note muted");
      this.#viewport.append(note);
    }
    note.textContent = messages[map.image.state] ?? "";
  }

  // ---- Drawing ----

  #render(): void {
    const map = this.#map;
    if (!map) return;
    Object.assign(this.#world.style, { width: `${map.image.width}px`, height: `${map.image.height}px` });
    this.#markers.replaceChildren(...this.#shown().map((m) => this.#renderMarker(m)));
    this.#schedule();
  }

  #shown(): Marker[] {
    return (this.#map?.markers ?? []).filter((m) => !this.#hiddenLayers.has(m.layerId ?? "none"));
  }

  #shownAreas(): Area[] {
    return (this.#map?.areas ?? []).filter((a) => !this.#hiddenLayers.has(a.layerId ?? "none"));
  }

  #renderMarker(m: Marker): HTMLElement {
    const el = document.createElement("button");
    el.type = "button";
    el.className = `map-marker color-${m.color}`;
    el.dataset.id = m.id;
    el.classList.toggle("selected", m.id === this.#selected);
    el.classList.toggle("restricted", m.restrictedTo.length > 0);
    el.title = m.label;
    const custom = m.icon.startsWith("icon:") ? this.#map?.customIcons.find((i) => i.value === m.icon) : undefined;
    if (custom) {
      // Custom icons stand on their own, their middle on the marker's place.
      el.classList.add("custom");
      const img = document.createElement("img");
      img.src = custom.url;
      img.alt = "";
      img.className = "map-marker-image";
      img.draggable = false;
      el.append(img);
    } else el.append(markerIcon(m.icon));
    const text = document.createElement("span");
    text.className = "map-marker-label";
    text.textContent = m.label;
    el.append(text);
    return el;
  }

  // One redraw per frame: the transform, the tiles in view, the grid and the markers' places.
  #schedule(): void {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#apply();
    });
  }

  #apply(): void {
    const map = this.#map;
    if (!map) return;
    const { x, y, scale } = this.#view;
    this.#world.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    this.#drawTiles();
    this.#drawGrid();
    this.#drawAreas();
    const w = map.image.width * scale;
    const h = map.image.height * scale;
    for (const el of this.#markers.children as HTMLCollectionOf<HTMLElement>) {
      const m = this.#marker(el.dataset.id!);
      if (!m) continue;
      el.style.transform = `translate(${Math.round(x + m.x * w)}px, ${Math.round(y + m.y * h)}px)`;
    }
    this.#placePanel();
  }

  // Deep Zoom tiles: the level that's at least as sharp as the screen, only those in view; a small level under them
  // all, so nothing is ever blank while tiles load.
  #drawTiles(): void {
    const image = this.#map!.image;
    if (image.state !== "ready" || !image.tiles) {
      this.#tiles.replaceChildren();
      this.#backdrop.replaceChildren();
      return;
    }
    const n = image.maxLevel;
    const backdropLevel = Math.max(0, n - Math.max(0, Math.ceil(Math.log2(Math.max(image.width, image.height) / 1024))));
    if (this.#tileLevel === -1) {
      this.#backdrop.replaceChildren(...this.#tileImages(backdropLevel, null));
      this.#tiles.replaceChildren();
    }
    const level = Math.min(n, Math.max(backdropLevel, n + Math.ceil(Math.log2(this.#view.scale * devicePixelRatio))));
    if (level !== this.#tileLevel) {
      this.#tileLevel = level;
      for (const img of [...this.#tiles.children]) if ((img as HTMLElement).dataset.level !== String(level)) img.remove();
    }
    if (level === backdropLevel) return this.#tiles.replaceChildren();
    const rect = this.#viewport.getBoundingClientRect();
    const { x, y, scale } = this.#view;
    const visible = { left: -x / scale, top: -y / scale, right: (rect.width - x) / scale, bottom: (rect.height - y) / scale };
    const have = new Set([...this.#tiles.children].map((el) => (el as HTMLElement).dataset.key));
    const fresh = this.#tileImages(level, visible).filter((img) => !have.has(img.dataset.key));
    // Tiles far out of view go, so a long session doesn't hold the whole map.
    const keep = new Set(this.#tileImages(level, grow(visible, 1)).map((img) => img.dataset.key));
    for (const el of [...this.#tiles.children]) if (!keep.has((el as HTMLElement).dataset.key)) el.remove();
    this.#tiles.append(...fresh);
  }

  #tileImages(level: number, within: { left: number; top: number; right: number; bottom: number } | null): HTMLImageElement[] {
    const image = this.#map!.image;
    const f = 2 ** (image.maxLevel - level);
    const levelW = Math.ceil(image.width / f);
    const levelH = Math.ceil(image.height / f);
    const size = image.tileSize;
    const cols = Math.ceil(levelW / size);
    const rows = Math.ceil(levelH / size);
    const span = size * f;
    const c0 = within ? Math.max(0, Math.floor(within.left / span)) : 0;
    const c1 = within ? Math.min(cols - 1, Math.floor(within.right / span)) : cols - 1;
    const r0 = within ? Math.max(0, Math.floor(within.top / span)) : 0;
    const r1 = within ? Math.min(rows - 1, Math.floor(within.bottom / span)) : rows - 1;
    const result: HTMLImageElement[] = [];
    for (let c = c0; c <= c1; c++)
      for (let r = r0; r <= r1; r++) {
        const img = document.createElement("img");
        img.alt = "";
        img.draggable = false;
        img.decoding = "async";
        img.dataset.key = `${level}/${c}_${r}`;
        img.dataset.level = String(level);
        const w = Math.min(size, levelW - c * size);
        const h = Math.min(size, levelH - r * size);
        // Half a tile pixel more on each side, so no seams show between tiles.
        Object.assign(img.style, { left: `${c * span}px`, top: `${r * span}px`, width: `${(w + 0.5) * f}px`, height: `${(h + 0.5) * f}px` });
        img.src = `${image.tiles}${level}/${c}_${r}.webp`;
        result.push(img);
      }
    return result;
  }

  // The grid, drawn in screen pixels so its lines stay thin at any zoom; left out when its cells get too small to see.
  #drawGrid(): void {
    const map = this.#map!;
    const grid = map.grid;
    const rect = this.#viewport.getBoundingClientRect();
    this.#grid.setAttribute("width", String(rect.width));
    this.#grid.setAttribute("height", String(rect.height));
    const { x, y, scale } = this.#view;
    if (grid.kind === "none" || grid.size * scale < 8 || map.image.state !== "ready") return this.#grid.replaceChildren();
    const toScreen = (px: number, py: number): [number, number] => [x + px * scale, y + py * scale];
    const area = {
      left: Math.max(0, -x / scale),
      top: Math.max(0, -y / scale),
      right: Math.min(map.image.width, (rect.width - x) / scale),
      bottom: Math.min(map.image.height, (rect.height - y) / scale),
    };
    let d = "";
    if (grid.kind === "square") {
      for (const [ax, ay, bx, by] of squareLines(grid, area)) {
        const [sx, sy] = toScreen(ax, ay);
        const [ex, ey] = toScreen(bx, by);
        d += `M${sx.toFixed(1)} ${sy.toFixed(1)}L${ex.toFixed(1)} ${ey.toFixed(1)}`;
      }
    } else {
      for (const { corners } of hexCentres(grid, area)) {
        d += corners.map(([cx, cy], i) => {
          const [sx, sy] = toScreen(cx, cy);
          return `${i === 0 ? "M" : "L"}${sx.toFixed(1)} ${sy.toFixed(1)}`;
        }).join("") + "Z";
      }
    }
    // The image's edge clips the grid.
    const [ix, iy] = toScreen(0, 0);
    const clip = `<clipPath id="map-clip-${map.id}"><rect x="${ix}" y="${iy}" width="${map.image.width * scale}" height="${map.image.height * scale}"/></clipPath>`;
    this.#grid.innerHTML = `<defs>${clip}</defs><path clip-path="url(#map-clip-${map.id})" d="${d}"/>`;
  }

  // Areas, drawn in screen pixels like the grid; the selected one with its handles (edit mode), and the one being drawn.
  #drawAreas(): void {
    const map = this.#map!;
    const rect = this.#viewport.getBoundingClientRect();
    this.#areas.setAttribute("width", String(rect.width));
    this.#areas.setAttribute("height", String(rect.height));
    const { x, y, scale } = this.#view;
    const [w, h] = [map.image.width, map.image.height];
    const at = ([px, py]: Point): string => `${(x + px * scale).toFixed(1)} ${(y + py * scale).toFixed(1)}`;
    const path = (points: Point[], close: boolean): string => `M${points.map(at).join("L")}${close ? "Z" : ""}`;
    let svg = "";
    for (const a of this.#shownAreas()) {
      const classes = `map-area color-${a.color}${a.id === this.#selected ? " selected" : ""}${a.restrictedTo ? " restricted" : ""}`;
      svg += `<g class="${classes}" data-id="${a.id}"><path d="${path(outline(a, w, h), true)}"/>`;
      if (a.label) {
        const [mx, my] = middle(a, w, h);
        svg += `<text x="${(x + mx * scale).toFixed(1)}" y="${(y + my * scale).toFixed(1)}">${escapeXml(a.label)}</text>`;
      }
      svg += "</g>";
    }
    const selected = this.#editing && this.#selected ? this.#area(this.#selected) : undefined;
    if (selected)
      for (const handle of handles(selected, w, h))
        svg += `<circle class="map-handle" data-area="${selected.id}" data-handle="${handle.key}" cx="${(x + handle.at[0] * scale).toFixed(1)}" cy="${(y + handle.at[1] * scale).toFixed(1)}" r="7"/>`;
    const drawn = this.#drawnShape();
    if (drawn) svg += `<g class="map-area map-drawing"><path d="${path(outline(drawn, w, h), true)}"/></g>`;
    else if (this.#drawing?.shape === "freeform" && this.#drawing.corners.length) {
      const corners = this.#drawing.cursor ? [...this.#drawing.corners, this.#drawing.cursor] : this.#drawing.corners;
      svg += `<g class="map-area map-drawing"><path d="${path(corners, false)}"/></g>`;
      svg += `<circle class="map-handle first" cx="${(x + this.#drawing.corners[0]![0] * scale).toFixed(1)}" cy="${(y + this.#drawing.corners[0]![1] * scale).toFixed(1)}" r="7"/>`;
    }
    this.#areas.innerHTML = svg;
  }

  // The shape being dragged out, in stored form; null until there's a drag (or for a freeform shape, until it's closed).
  #drawnShape(): AreaShape | null {
    const d = this.#drawing;
    const map = this.#map;
    if (!d || !map || !d.start || !d.cursor || d.shape === "freeform") return null;
    const [w, h] = [map.image.width, map.image.height];
    const [[sx, sy], [cx, cy]] = [d.start, d.cursor];
    const length = Math.hypot(cx - sx, cy - sy);
    if (length * this.#view.scale < 4) return null;
    if (d.shape === "circle") return { shape: "circle", points: [sx / w, sy / h], radius: length / w, angle: 0, spread: 60 };
    if (d.shape === "rectangle") return { shape: "rectangle", points: [sx / w, sy / h, cx / w, cy / h], radius: 0, angle: 0, spread: 60 };
    return { shape: "cone", points: [sx / w, sy / h], radius: length / w, angle: ((Math.atan2(cy - sy, cx - sx) * 180) / Math.PI + 360) % 360, spread: 60 };
  }

  // ---- View ----

  #zoomBy(factor: number, cx?: number, cy?: number): void {
    const rect = this.#viewport.getBoundingClientRect();
    const px = cx ?? rect.width / 2;
    const py = cy ?? rect.height / 2;
    const scale = Math.min(this.#maxScale(), Math.max(this.#minScale(), this.#view.scale * factor));
    const k = scale / this.#view.scale;
    this.#view = { x: px - (px - this.#view.x) * k, y: py - (py - this.#view.y) * k, scale };
    this.#schedule();
  }

  #fitScale(): number {
    const map = this.#map;
    const rect = this.#viewport.getBoundingClientRect();
    if (!map || rect.width === 0 || map.image.width === 0) return 1;
    return Math.min(rect.width / map.image.width, rect.height / map.image.height);
  }

  #minScale(): number {
    return this.#fitScale() / 2;
  }

  // Up to four screen pixels per image pixel.
  #maxScale(): number {
    return Math.max(4, this.#fitScale());
  }

  #fit(): void {
    const map = this.#map;
    const rect = this.#viewport.getBoundingClientRect();
    if (!map || rect.width === 0) return;
    const scale = this.#fitScale();
    this.#view = { scale, x: (rect.width - map.image.width * scale) / 2, y: (rect.height - map.image.height * scale) / 2 };
    this.#schedule();
  }

  // Glides to a place on the map (zooming in if the map is far out), then does what's next (shows what's there).
  #focusOn(m: { x: number; y: number }, then?: () => void): void {
    const map = this.#map!;
    const rect = this.#viewport.getBoundingClientRect();
    const scale = Math.max(this.#view.scale, Math.min(1, this.#fitScale() * 3));
    const from = { ...this.#view };
    const to = { scale, x: rect.width / 2 - m.x * map.image.width * scale, y: rect.height / 2 - m.y * map.image.height * scale };
    const start = performance.now();
    const step = (now: number): void => {
      const t = Math.min(1, (now - start) / 350);
      const ease = 1 - (1 - t) ** 3;
      this.#view = { scale: from.scale + (to.scale - from.scale) * ease, x: from.x + (to.x - from.x) * ease, y: from.y + (to.y - from.y) * ease };
      this.#apply();
      if (t < 1) requestAnimationFrame(step);
      else then?.();
    };
    requestAnimationFrame(step);
  }

  #toggleFull(): void {
    this.classList.toggle("is-full");
    requestAnimationFrame(() => this.#fit());
  }

  // Where a point on screen is on the image (0–1).
  #toImage(clientX: number, clientY: number): [number, number] {
    const map = this.#map!;
    const rect = this.#viewport.getBoundingClientRect();
    return [
      (clientX - rect.left - this.#view.x) / this.#view.scale / map.image.width,
      (clientY - rect.top - this.#view.y) / this.#view.scale / map.image.height,
    ];
  }

  // Where a point on screen is on the image, in its pixels.
  #toPixels(clientX: number, clientY: number): Point {
    const [rx, ry] = this.#toImage(clientX, clientY);
    return [rx * this.#map!.image.width, ry * this.#map!.image.height];
  }

  #snapped(x: number, y: number): [number, number] {
    const map = this.#map!;
    if (!this.#snap || map.grid.kind === "none") return [x, y];
    const [sx, sy] = snapToGrid(map.grid, x * map.image.width, y * map.image.height);
    return [sx / map.image.width, sy / map.image.height];
  }

  // ---- Interaction ----

  #wire(): void {
    type Drag = {
      kind: "pan" | "move" | "draw" | "handle" | "area";
      id?: string;
      areaId?: string;
      handle?: string;
      original?: Area;
      startX: number;
      startY: number;
      ox: number;
      oy: number;
      moved: boolean;
    };
    let drag: Drag | null = null;
    const pointers = new Map<number, { x: number; y: number }>();
    let pinch: { distance: number; scale: number } | null = null;

    this.#viewport.addEventListener("pointerdown", (event) => {
      if (event.button !== 0 && event.pointerType === "mouse") return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.#viewport.setPointerCapture(event.pointerId);
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinch = { distance: Math.hypot(a!.x - b!.x, a!.y - b!.y), scale: this.#view.scale };
        drag = null;
        return;
      }
      const target = event.target as Element;
      const base = { startX: event.clientX, startY: event.clientY, ox: this.#view.x, oy: this.#view.y, moved: false };
      // Drawing a circle, rectangle or cone: the drag is the shape. (A freeform shape is made of clicks.)
      if (this.#drawing && this.#drawing.shape !== "freeform") {
        let start = this.#toPixels(event.clientX, event.clientY);
        if (this.#snap) {
          const [sx, sy] = this.#snapped(start[0] / this.#map!.image.width, start[1] / this.#map!.image.height);
          start = [sx * this.#map!.image.width, sy * this.#map!.image.height];
        }
        Object.assign(this.#drawing, { start, cursor: start });
        drag = { kind: "draw", ...base };
        return;
      }
      const handleEl = target.closest<SVGElement>(".map-handle[data-handle]");
      if (handleEl && this.#editing) {
        const area = this.#area(handleEl.dataset.area!);
        if (area) drag = { kind: "handle", areaId: area.id, handle: handleEl.dataset.handle, original: { ...area, points: [...area.points] }, ...base };
        return;
      }
      const areaEl = !this.#drawing && !this.#placing ? target.closest<SVGElement>(".map-area[data-id]") : null;
      const area = areaEl ? this.#area(areaEl.dataset.id!) : undefined;
      if (area) {
        drag = this.#editing && area.id === this.#selected
          ? { kind: "area", areaId: area.id, original: { ...area, points: [...area.points] }, ...base }
          : { kind: "pan", areaId: area.id, ...base };
        return;
      }
      const markerEl = target.closest<HTMLElement>(".map-marker");
      const marker = markerEl && !this.#drawing ? this.#marker(markerEl.dataset.id!) : undefined;
      drag = marker && this.#editing
        ? { kind: "move", id: marker.id, startX: event.clientX, startY: event.clientY, ox: marker.x, oy: marker.y, moved: false }
        : { kind: "pan", id: marker?.id, startX: event.clientX, startY: event.clientY, ox: this.#view.x, oy: this.#view.y, moved: false };
    });

    this.#viewport.addEventListener("pointermove", (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pinch && pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        const rect = this.#viewport.getBoundingClientRect();
        const factor = (Math.hypot(a!.x - b!.x, a!.y - b!.y) / pinch.distance) * pinch.scale / this.#view.scale;
        this.#zoomBy(factor, (a!.x + b!.x) / 2 - rect.left, (a!.y + b!.y) / 2 - rect.top);
        return;
      }
      if (!drag) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (!drag.moved && Math.abs(dx) + Math.abs(dy) < 4) return;
      drag.moved = true;
      if (drag.kind === "pan") {
        this.#view.x = drag.ox + dx;
        this.#view.y = drag.oy + dy;
        return this.#schedule();
      }
      const map = this.#map!;
      const [w, h] = [map.image.width, map.image.height];
      if (drag.kind === "draw" && this.#drawing) {
        this.#drawing.cursor = this.#toPixels(event.clientX, event.clientY);
        const shape = this.#drawnShape();
        if (shape) this.#say(sizeText(shape, w, h, map.grid));
        return this.#schedule();
      }
      if ((drag.kind === "handle" || drag.kind === "area") && drag.original) {
        const area = this.#area(drag.areaId!);
        if (!area) return;
        const [px, py] = this.#toPixels(event.clientX, event.clientY);
        const shaped = drag.kind === "handle"
          ? dragHandle(drag.original, drag.handle!, px, py, w, h)
          : moveArea(drag.original, dx / this.#view.scale, dy / this.#view.scale, w, h);
        Object.assign(area, { points: shaped.points, radius: shaped.radius, angle: shaped.angle });
        this.#say(sizeText(area, w, h, map.grid));
        return this.#schedule();
      }
      const marker = this.#marker(drag.id!);
      if (!marker) return;
      marker.x = clamp01(drag.ox + dx / this.#view.scale / map.image.width);
      marker.y = clamp01(drag.oy + dy / this.#view.scale / map.image.height);
      this.#schedule();
    });

    const release = (event: PointerEvent): void => {
      pointers.delete(event.pointerId);
      if (pinch) {
        if (pointers.size < 2) pinch = null;
        return;
      }
      const d = drag;
      drag = null;
      if (!d) return;
      if (d.kind === "draw") {
        const shape = this.#drawnShape();
        if (shape) return void this.#addArea(shape);
        if (this.#drawing) Object.assign(this.#drawing, { start: undefined, cursor: undefined });
        return this.#say(SHAPE_HINTS[this.#drawing?.shape ?? "circle"]!);
      }
      if ((d.kind === "handle" || d.kind === "area") && d.moved) {
        const area = this.#area(d.areaId!);
        if (area) void this.#call<Area>("UpdateArea", { area: area.id }, { points: area.points, radius: area.radius || null, angle: area.angle });
        return;
      }
      if (!d.moved && this.#drawing?.shape === "freeform" && event.type === "pointerup") return this.#freeformClick(event.clientX, event.clientY);
      const marker = d.id ? this.#marker(d.id) : undefined;
      const area = d.areaId ? this.#area(d.areaId) : undefined;
      if (!d.moved) {
        if (marker) return this.#click(marker);
        if (area) return this.#clickArea(area);
        if (this.#placing && event.type === "pointerup") return void this.#addMarkerAt(event.clientX, event.clientY);
        this.#closePanel();
        return;
      }
      if (marker && d.kind === "move") {
        [marker.x, marker.y] = this.#snapped(marker.x, marker.y);
        this.#schedule();
        void this.#call<Marker>("UpdateMarker", { marker: marker.id }, { x: marker.x, y: marker.y });
      }
    };
    this.#viewport.addEventListener("pointerup", release);
    // A freeform shape's next side follows the pointer.
    this.#viewport.addEventListener("pointermove", (event) => {
      if (this.#drawing?.shape !== "freeform" || pointers.size > 0) return;
      this.#drawing.cursor = this.#toPixels(event.clientX, event.clientY);
      this.#schedule();
    });
    this.#viewport.addEventListener("pointercancel", release);

    this.#viewport.addEventListener("dblclick", (event) => {
      if ((event.target as HTMLElement).closest(".map-marker") || this.#drawing) return;
      const rect = this.#viewport.getBoundingClientRect();
      this.#zoomBy(2, event.clientX - rect.left, event.clientY - rect.top);
    });

    this.#viewport.addEventListener(
      "wheel",
      (event) => {
        // Plain scrolling scrolls the page; Ctrl + scroll (or pinch) zooms, and in full screen scrolling zooms too.
        if (!event.ctrlKey && !this.classList.contains("is-full")) return;
        event.preventDefault();
        const rect = this.#viewport.getBoundingClientRect();
        this.#zoomBy(Math.exp(-event.deltaY * (event.deltaMode === 1 ? 0.05 : 0.002)), event.clientX - rect.left, event.clientY - rect.top);
      },
      { passive: false },
    );

    this.addEventListener("keydown", (event) => {
      if ((event.target as HTMLElement).closest("input, textarea, .board-panel")) {
        if (event.key === "Escape") this.#closePanel();
        return;
      }
      const step = 60;
      const moves: Record<string, [number, number]> = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
      if (moves[event.key]) {
        event.preventDefault();
        this.#view.x += moves[event.key]![0];
        this.#view.y += moves[event.key]![1];
        this.#schedule();
      } else if (event.key === "+" || event.key === "=") this.#zoomBy(1.4);
      else if (event.key === "-") this.#zoomBy(1 / 1.4);
      else if (event.key === "Enter" && this.#drawing?.shape === "freeform") void this.#finishFreeform();
      else if (event.key === "Escape") {
        if (this.#drawing) this.#stopDrawing("");
        else if (this.#placing) this.#togglePlacing();
        else if (!this.#panel.hidden) this.#closePanel();
        else if (this.classList.contains("is-full")) this.#toggleFull();
      }
    });
  }

  #click(marker: Marker): void {
    this.#selected = marker.id;
    if (this.#editing) this.#openMarkerEditor(marker);
    else this.#showDetails(marker);
  }

  #clickArea(area: Area): void {
    this.#selected = area.id;
    if (this.#editing) this.#openAreaEditor(area);
    else this.#showDetails(area, "Area");
  }

  #togglePlacing(): void {
    this.#placing = !this.#placing;
    if (!this.#placing) this.#placePage = null;
    if (this.#drawing) this.#stopDrawing("");
    this.#placeButton.setAttribute("aria-pressed", String(this.#placing));
    this.classList.toggle("is-placing", this.#placing);
    this.#say(this.#placing ? "Click the map where the marker goes (Esc to cancel)." : "");
  }

  // "Place on a map": the next click puts the page's (or one of its sections') marker there, moving it if it has one here.
  #startPlacingPage(id: string, title: string, section: string): void {
    if (!this.#placing) this.#togglePlacing();
    this.#placePage = { id, title, section };
    const existing = this.#map!.markers.find((m) => m.page?.id === id && (m.section ?? "") === section);
    if (existing) {
      this.#selected = existing.id;
      this.#render();
    }
    this.#say(existing ? `Click the new place for “${title}” (its marker is the one marked now).` : `Click where “${title}” is on the map.`);
  }

  async #addMarkerAt(clientX: number, clientY: number): Promise<void> {
    const placing = this.#placePage;
    this.#togglePlacing();
    const [rx, ry] = this.#toImage(clientX, clientY);
    if (rx < 0 || rx > 1 || ry < 0 || ry > 1) return this.#say("That's outside the map.");
    const [x, y] = this.#snapped(rx, ry);
    const existing = placing ? this.#map!.markers.find((m) => m.page?.id === placing.id && (m.section ?? "") === placing.section) : undefined;
    const added = existing
      ? await this.#call<Marker>("UpdateMarker", { marker: existing.id }, { x, y })
      : await this.#call<Marker>("AddMarker", {}, { x, y, label: "", icon: "pin", color: "red", pageId: placing?.id ?? null, section: placing?.section || null });
    if (!added) return;
    this.#replace(added);
    this.#selected = added.id;
    this.#openMarkerEditor(added);
    if (placing) this.#say(`Placed “${placing.title}”.`);
  }

  // ---- Drawing areas ----

  #startDrawing(shape: string): void {
    this.#areaMenu.open = false;
    if (this.#placing) this.#togglePlacing();
    this.#closePanel();
    this.#drawing = { shape, corners: [] };
    this.classList.add("is-placing");
    this.#say(SHAPE_HINTS[shape]!);
  }

  #stopDrawing(message: string): void {
    this.#drawing = null;
    this.classList.remove("is-placing");
    this.#say(message);
    this.#schedule();
  }

  // A click while drawing a freeform shape: a new corner, or (on the first corner) closing it.
  #freeformClick(clientX: number, clientY: number): void {
    const d = this.#drawing!;
    const at = this.#toPixels(clientX, clientY);
    const first = d.corners[0];
    if (first && d.corners.length >= 3 && Math.hypot(at[0] - first[0], at[1] - first[1]) * this.#view.scale < 12) return void this.#finishFreeform();
    d.corners.push(at);
    this.#say(d.corners.length < 3 ? SHAPE_HINTS.freeform! : `${d.corners.length} corners. Click the first corner, or press Enter, to close the shape.`);
    this.#schedule();
  }

  async #finishFreeform(): Promise<void> {
    const d = this.#drawing!;
    const map = this.#map!;
    if (d.corners.length < 3) return this.#say("A freeform shape needs at least three corners.");
    const points = d.corners.flatMap(([px, py]) => [px / map.image.width, py / map.image.height]);
    await this.#addArea({ shape: "freeform", points, radius: 0, angle: 0, spread: 60 });
  }

  async #addArea(shape: AreaShape): Promise<void> {
    this.#stopDrawing("");
    const added = await this.#call<Area>("AddArea", {}, { ...shape, radius: shape.radius || null, color: "red" });
    if (!added) return;
    this.#replaceArea(added);
    this.#selected = added.id;
    this.#openAreaEditor(added);
  }

  async #deleteMap(): Promise<void> {
    if (!window.confirm("Delete this map, its markers and layers for good? It's no longer in the page's text.")) return;
    if (await this.#call("DeleteMap", {}, {})) this.closest("[data-map-entry]")?.remove() ?? this.remove();
  }

  // ---- Layers on and off (everyone; remembered on this device only while the page is open) ----

  #renderLayerMenu(): void {
    const map = this.#map!;
    const menu = this.#layerMenu;
    menu.hidden = map.layers.length === 0;
    const summary = document.createElement("summary");
    summary.textContent = "Layers ▾";
    summary.title = "Show or hide layers";
    const list = div("dropdown-list map-layer-list");
    const entries: Array<[string, string]> = [["", "Markers and areas on no layer"], ...map.layers.map((l): [string, string] => [l.id, l.name])];
    for (const [id, name] of entries) {
      const row = document.createElement("label");
      row.className = "check";
      const box = document.createElement("input");
      box.type = "checkbox";
      box.checked = !this.#hiddenLayers.has(id || "none");
      box.addEventListener("change", () => {
        if (box.checked) this.#hiddenLayers.delete(id || "none");
        else this.#hiddenLayers.add(id || "none");
        this.#render();
      });
      row.append(box, ` ${name}`);
      list.append(row);
    }
    menu.replaceChildren(summary, list);
  }

  // ---- The floating panel ----

  #placePanel(): void {
    if (this.#panel.hidden) return;
    const stage = this.#panel.parentElement!.getBoundingClientRect();
    if (stage.width === 0) return;
    const width = Math.min(352, stage.width - 16);
    this.#panel.style.width = `${width}px`;
    const id = this.#panel.dataset.marker;
    const el = id ? this.#markers.querySelector<HTMLElement>(`.map-marker[data-id="${id}"]`) ?? this.#areas.querySelector<SVGElement>(`.map-area[data-id="${id}"]`) : null;
    let left = stage.width - width - 8;
    let top = 8;
    if (el) {
      const r = el.getBoundingClientRect();
      const right = r.right - stage.left + 12;
      const leftSide = r.left - stage.left - width - 12;
      left = right + width <= stage.width - 8 ? right : leftSide >= 8 ? leftSide : Math.max(8, stage.width - width - 8);
      top = Math.min(Math.max(8, r.top - stage.top - 20), Math.max(8, stage.height - this.#panel.offsetHeight - 8));
    }
    Object.assign(this.#panel.style, { left: `${left}px`, top: `${top}px` });
  }

  #showPanel(markerId: string | null, heading: string): HTMLFormElement {
    const form = this.#panel;
    form.replaceChildren();
    form.hidden = false;
    form.dataset.marker = markerId ?? "";
    const head = div("board-panel-head");
    const h = document.createElement("h3");
    h.textContent = heading;
    const close = button("×", "Close", () => this.#closePanel());
    close.className = "link board-panel-close";
    head.append(h, close);
    form.append(head);
    form.onsubmit = (event) => event.preventDefault();
    this.#render();
    requestAnimationFrame(() => this.#placePanel());
    return form;
  }

  #closePanel(): void {
    if (this.#panel.hidden) return;
    this.#panel.hidden = true;
    this.#panel.dataset.marker = "";
    this.#selected = null;
    this.#render();
  }

  // View mode: what a marker or area says and the page it stands for.
  #showDetails(m: Marker | Area, kind = "Marker"): void {
    const form = this.#showPanel(m.id, m.label || kind);
    if (m.bodyHtml) {
      const body = div("board-body prose");
      body.innerHTML = m.bodyHtml; // rendered and sanitized by the server
      form.append(body);
    }
    if (m.page) {
      const p = document.createElement("p");
      const link = document.createElement("a");
      link.href = m.page.url;
      link.textContent = m.sectionTitle ? `${m.page.title} › ${m.sectionTitle}` : m.page.title;
      p.append("Stands for ", link);
      form.append(p);
      if (m.page.excerpt) {
        const excerpt = document.createElement("p");
        excerpt.className = "board-excerpt";
        excerpt.textContent = m.page.excerpt;
        form.append(excerpt);
      }
    }
    const layer = this.#map!.layers.find((l) => l.id === m.layerId);
    if (layer) {
      const note = document.createElement("p");
      note.className = "muted small";
      note.textContent = `Layer: ${layer.name}`;
      form.append(note);
    }
    if (!m.bodyHtml && !m.page && !layer) {
      const empty = document.createElement("p");
      empty.className = "muted small";
      empty.textContent = `Nothing more about this ${kind.toLowerCase()}.`;
      form.append(empty);
    }
  }

  // The fields markers and areas share: label, the page they stand for, note (first); layer, who sees it, no AI (last).
  #sharedFields(item: Marker | Area, kind: string): { first: HTMLElement[]; last: HTMLElement[]; values: (form: HTMLFormElement) => Record<string, unknown> } {
    const map = this.#map!;
    const labelInput = textInput("label", item.ownLabel, item.page ? `${item.page.title} (the page's title)` : kind === "marker" ? "e.g. Elysium" : "e.g. The docks");
    const pageField = document.createElement("input");
    pageField.type = "hidden";
    pageField.name = "pageId";
    pageField.value = item.page?.id ?? "";
    const picker = document.createElement("p9-autocomplete");
    picker.dataset.url = `${this.#base}lookup`;
    picker.dataset.kinds = "pages";
    picker.dataset.pickPage = "pageId";
    const pageInput = document.createElement("input");
    pageInput.type = "text";
    pageInput.placeholder = item.pageLocked ? "A page you can't see (kept as it is)" : item.page?.title ?? "Find a page… (optional)";
    pageInput.autocomplete = "off";
    pageInput.setAttribute("aria-label", "Page it stands for");
    picker.append(pageInput);
    const pageRow = div("map-page-row");
    pageRow.append(picker);
    // Which section of the page (its headings, as the person sees it), so a page can have a marker per section.
    const sectionField = document.createElement("input");
    sectionField.type = "hidden";
    sectionField.value = item.section ?? "";
    const sectionMenu = document.createElement("details");
    sectionMenu.className = "dropdown map-section-menu";
    const sectionSummary = document.createElement("summary");
    const sectionList = div("dropdown-list map-layer-list");
    sectionMenu.append(sectionSummary, sectionList);
    const showSection = (title: string): void => void (sectionSummary.textContent = `Section: ${title} ▾`);
    const loadSections = async (pageId: string, keep: boolean): Promise<void> => {
      if (!keep) sectionField.value = "";
      sectionMenu.hidden = !pageId;
      if (!pageId) return;
      showSection(keep && item.sectionTitle ? item.sectionTitle : "the whole page");
      const sections: Array<{ id: string; title: string }> =
        await fetch(`${this.#base}maps?handler=Sections&of=${encodeURIComponent(pageId)}`).then((r) => r.json()).catch(() => []);
      const pickSection = (id: string, title: string): void => {
        sectionField.value = id;
        showSection(title);
        sectionMenu.open = false;
      };
      sectionList.replaceChildren(button("The whole page", "", () => pickSection("", "the whole page")),
        ...sections.map((x) => button(x.title, "", () => pickSection(x.id, x.title))));
      if (sections.length === 0) sectionList.append(Object.assign(document.createElement("p"), { className: "muted small", textContent: "This page has no headings." }));
    };
    picker.addEventListener("p9-select", (event) => {
      const id = (event as CustomEvent<{ id?: string }>).detail?.id ?? "";
      void loadSections(id, false);
    });
    void loadSections(item.page?.id ?? "", true);
    let clearPage = false;
    if (item.page || item.pageLocked) {
      const unlink = button("Unlink", `This ${kind} no longer stands for a page`, () => {
        clearPage = true;
        pageField.value = "";
        pageInput.value = "";
        pageInput.placeholder = "No page";
        void loadSections("", false);
      });
      unlink.className = "link small";
      pageRow.append(unlink);
    }
    const body = document.createElement("textarea");
    body.name = "body";
    body.rows = 4;
    body.value = item.body;
    body.placeholder = "Note: Markdown, [[ page links";

    const last: HTMLElement[] = [];
    if (map.layers.length) last.push(choices("Layer", "layerId", ["", ...map.layers.map((l) => l.id)], item.layerId ?? "",
      (v) => (v ? map.layers.find((l) => l.id === v)?.name ?? v : "None")));
    let restrictedTo = item.restrictedTo;
    const { element: who } = principalsInput({
      lookupUrl: `${this.#base}lookup`, me: this.#me(), value: item.restrictedTo,
      placeholder: "everyone who sees the page", everyone: "Everyone who sees the page",
      onChange: (value) => (restrictedTo = value), onInput: (value) => (restrictedTo = value),
    });
    const noAi = document.createElement("input");
    noAi.type = "checkbox";
    noAi.checked = item.noAi;
    const noAiLabel = document.createElement("label");
    noAiLabel.className = "check";
    noAiLabel.append(noAi, " Never show to AI");
    last.push(labelled("Visible to", who), noAiLabel);
    requestAnimationFrame(() => labelInput.focus({ preventScroll: true }));
    return {
      first: [labelled("Label", labelInput), labelled("Stands for a page", pageRow), pageField, sectionMenu, labelled("Note", body)],
      last,
      values: (form) => {
        const data = new FormData(form);
        return {
          label: labelInput.value,
          body: body.value,
          color: String(data.get("color") ?? "red"),
          layerId: map.layers.length ? String(data.get("layerId") ?? "") || EMPTY_ID : null,
          pageId: pageField.value || (clearPage ? EMPTY_ID : null),
          section: sectionField.value,
          restrictedTo,
          noAi: noAi.checked,
        };
      },
    };
  }

  // The editor's buttons: Save, Close and Delete.
  #editorActions(form: HTMLFormElement, save: () => Promise<void>, deleteLabel: string, remove: () => Promise<void>): void {
    const actions = div("form-actions");
    const saveButton = button("Save", "", () => void save());
    saveButton.className = "button";
    const del = button(deleteLabel, deleteLabel, () => void remove());
    del.className = "link danger";
    actions.append(saveButton, button("Close", "", () => this.#closePanel()), del);
    form.append(actions);
  }

  // Edit mode: a marker's label, note, page, look, layer and who sees it.
  #openMarkerEditor(m: Marker): void {
    const map = this.#map!;
    const form = this.#showPanel(m.id, "Marker");
    const fields = div("stack");
    const shared = this.#sharedFields(m, "marker");
    const style = div("board-style");
    style.append(
      iconChoices(map.icons, map.customIcons, m.icon),
      choices("Colour", "color", map.colors.filter((c) => c !== "default"), m.color, (v) => v),
    );
    fields.append(...shared.first, style, ...shared.last);
    form.append(fields);
    this.#editorActions(form, async () => {
      const payload = { ...shared.values(form), icon: String(new FormData(form).get("icon") ?? "pin") };
      const result = await this.#call<Marker>("UpdateMarker", { marker: m.id }, payload);
      if (!result) return;
      this.#closePanel();
      this.#replace(result);
      if (result.gone) this.#say("Saved. You can't see it any more, given who it's visible to.");
    }, "Delete marker", async () => {
      if (!window.confirm(`Delete the marker “${m.label}”?`)) return;
      if (await this.#call("DeleteMarker", { marker: m.id }, {})) {
        this.#closePanel();
        this.#replace({ ...m, gone: true });
      }
    });
  }

  // Edit mode: an area's label, note, page, colour, layer and who sees it; a rectangle's turn, a cone's direction and width.
  #openAreaEditor(a: Area): void {
    const map = this.#map!;
    const form = this.#showPanel(a.id, SHAPE_LABELS[a.shape] ?? "Area");
    const size = document.createElement("p");
    size.className = "muted small";
    size.textContent = `${sizeText(a, map.image.width, map.image.height, map.grid)}. Drag it to move it; drag its handles to change its shape.`;
    const fields = div("stack");
    const shared = this.#sharedFields(a, "area");
    const numbers = div("map-grid-numbers");
    const angle = numberInput("angle", a.angle, a.shape === "cone" ? "Where the cone points, in degrees clockwise from pointing right" : "How far the rectangle is turned, in degrees");
    const spread = numberInput("spread", a.spread, "How wide the cone is, in degrees");
    if (a.shape === "rectangle") numbers.append(labelled("Turned (°)", angle));
    if (a.shape === "cone") numbers.append(labelled("Points to (°)", angle), labelled("Width (°)", spread));
    // The shape follows the numbers as they're typed.
    numbers.addEventListener("input", () => {
      a.angle = Number(angle.value) || 0;
      a.spread = Math.min(359, Math.max(1, Number(spread.value) || 60));
      this.#schedule();
    });
    fields.append(size, ...shared.first, choices("Colour", "color", map.colors.filter((c) => c !== "default"), a.color, (v) => v), numbers, ...shared.last);
    form.append(fields);
    this.#editorActions(form, async () => {
      const payload = { ...shared.values(form), angle: Number(angle.value) || 0, spread: a.shape === "cone" ? Number(spread.value) || 60 : null };
      const result = await this.#call<Area>("UpdateArea", { area: a.id }, payload);
      if (!result) return;
      this.#closePanel();
      this.#replaceArea(result);
      if (result.gone) this.#say("Saved. You can't see it any more, given who it's visible to.");
    }, "Delete area", async () => {
      if (!window.confirm(`Delete the area “${a.label || SHAPE_LABELS[a.shape]}”?`)) return;
      if (await this.#call("DeleteArea", { area: a.id }, {})) {
        this.#closePanel();
        this.#replaceArea({ ...a, gone: true });
      }
    });
  }

  // Edit mode: custom icons for this map, and adding new ones (to this page, a page above it, or every map in admin mode).
  #openIcons(): void {
    const map = this.#map!;
    const form = this.#showPanel(null, "Icons");
    const intro = document.createElement("p");
    intro.className = "muted small";
    intro.textContent = "Your own marker icons. An icon added on a page can be used on every map on that page and the pages below it, and is seen by those who see that page.";
    form.append(intro);
    const list = div("stack map-icon-list");
    for (const icon of map.customIcons) {
      const row = div("map-icon-row");
      const img = document.createElement("img");
      img.src = icon.url;
      img.alt = "";
      const name = document.createElement("span");
      name.textContent = icon.name;
      const where = document.createElement("span");
      where.className = "muted small";
      where.textContent = icon.onPage ? `on ${icon.onPage}` : "every map";
      row.append(img, name, where);
      if (icon.canChange) {
        const del = button("Delete", "Delete this icon; markers using it show a pin", async () => {
          if (!window.confirm(`Delete the icon “${icon.name}”? Markers using it show a pin instead.`)) return;
          if (await this.#call("DeleteIcon", { icon: icon.value.slice(5) }, {})) {
            await this.#load();
            this.#openIcons();
          }
        });
        del.className = "link danger small";
        row.append(del);
      }
      list.append(row);
    }
    if (map.customIcons.length === 0) list.append(Object.assign(document.createElement("p"), { className: "muted small", textContent: "No icons yet." }));
    form.append(list);
    if (map.iconPlaces?.length) {
      const file = document.createElement("input");
      file.type = "file";
      file.accept = "image/png,image/jpeg,image/gif,image/webp,image/avif";
      const name = textInput("iconName", "", "Name, e.g. Haven");
      const place = choices("For maps on", "iconPlace", map.iconPlaces.map((p) => p.value), map.iconPlaces[0]!.value,
        (v) => map.iconPlaces!.find((p) => p.value === v)?.label ?? v);
      const add = button("Add icon", "Upload the image as an icon (cut to 64 pixels)", async () => {
        if (!file.files?.[0]) return this.#say("Pick an image first.");
        const body = new FormData();
        body.append("file", file.files[0]);
        body.append("name", name.value);
        body.append("place", String(new FormData(form).get("iconPlace") ?? ""));
        try {
          const response = await fetch(`${this.#url}?handler=AddIcon`, { method: "POST", headers: { RequestVerificationToken: token() }, body });
          const result = (await response.json().catch(() => ({}))) as { error?: string };
          if (!response.ok) return this.#say(result.error ?? "That didn't work.");
        } catch {
          return this.#say("Couldn't upload; check your connection.");
        }
        this.#say("Icon added.");
        await this.#load();
        this.#openIcons();
      });
      add.className = "button";
      form.append(labelled("New icon (a small picture; transparent PNGs look best)", file), labelled("Name", name), place, add);
    }
    const actions = div("form-actions");
    actions.append(button("Close", "", () => this.#closePanel()));
    form.append(actions);
  }

  // Edit mode: the map's name, image and grid, with the grid shown as it's typed.
  #openSettings(): void {
    const map = this.#map!;
    const saved = { ...map.grid };
    const form = this.#showPanel(null, "Map");
    const fields = div("stack");
    const title = textInput("title", map.title, "Map name");
    fields.append(labelled("Name", title));

    // The image: one of this page's images (or one just uploaded in the editor).
    const imageRow = div("map-image-row");
    const current = document.createElement("span");
    current.textContent = map.image.name ?? "No image";
    let attachmentId: string | null = null;
    const pick = document.createElement("details");
    pick.className = "dropdown";
    const pickSummary = document.createElement("summary");
    pickSummary.textContent = "Change image ▾";
    const pickList = div("dropdown-list map-image-list");
    pick.append(pickSummary, pickList);
    pick.addEventListener("toggle", async () => {
      if (!pick.open) return;
      pickList.textContent = "Loading…";
      const query = new URLSearchParams({ handler: "Pick", images: "true", page: map.pageId });
      const files = (await fetch(`${this.#base}files?${query}`).then((r) => r.json()).catch(() => [])) as Array<{ id: string; name: string; where: string; sharing: string }>;
      const usable = files.filter((f) => f.where === "this page" || f.where === "not on a page yet");
      pickList.replaceChildren(...usable.map((f) => button(`${f.name} (${f.sharing})`, "", () => {
        attachmentId = f.id;
        current.textContent = `${f.name} (saved with Save)`;
        pick.open = false;
      })));
      if (usable.length === 0) pickList.textContent = "No images on this page. Upload one in the page's text (Insert → Image), then pick it here.";
    });
    imageRow.append(current, pick);
    fields.append(labelled("Image", imageRow));

    const grid = div("map-grid-fields");
    const kind = choices("Grid", "gridKind", map.gridKinds.slice().sort((a, b) => Object.keys(GRID_LABELS).indexOf(a) - Object.keys(GRID_LABELS).indexOf(b)),
      map.grid.kind, (v) => GRID_LABELS[v] ?? v);
    const size = numberInput("gridSize", map.grid.size, "Cell size, in the image's pixels (a hex: from one flat side to the other)");
    const ox = numberInput("gridOffsetX", map.grid.offsetX, "Moves the grid right");
    const oy = numberInput("gridOffsetY", map.grid.offsetY, "Moves the grid down");
    const scale = textInput("scaleText", map.grid.scale, "e.g. 1 hex = 5 miles");
    const numbers = div("map-grid-numbers");
    numbers.append(labelled("Cell size (px)", size), labelled("Offset right", ox), labelled("Offset down", oy));
    grid.append(kind, numbers, labelled("Scale", scale));
    fields.append(grid);
    const preview = (): void => {
      const data = new FormData(form);
      map.grid = { ...map.grid, kind: String(data.get("gridKind") ?? "none"), size: Number(size.value) || saved.size, offsetX: Number(ox.value) || 0, offsetY: Number(oy.value) || 0 };
      this.#setEditing(true);
      this.#schedule();
    };
    grid.addEventListener("input", preview);
    grid.addEventListener("click", () => requestAnimationFrame(preview));
    form.append(fields);

    const actions = div("form-actions");
    const saveButton = button("Save", "", async () => {
      const ok = await this.#call("Update", {}, {
        title: title.value, attachmentId, gridKind: map.grid.kind, gridSize: map.grid.size, gridOffsetX: map.grid.offsetX, gridOffsetY: map.grid.offsetY,
        scaleText: scale.value,
      });
      if (!ok) return;
      this.#closePanel();
      void this.#load();
    });
    saveButton.className = "button";
    actions.append(saveButton, button("Close", "", () => {
      map.grid = saved;
      this.#setEditing(true);
      this.#closePanel();
    }));
    form.append(actions);
  }

  // Edit mode: layers, each with its name and who sees it; one with markers can't be deleted.
  #openLayers(): void {
    const map = this.#map!;
    const form = this.#showPanel(null, "Layers");
    const intro = document.createElement("p");
    intro.className = "muted small";
    intro.textContent = "Markers can sit on layers, shown or hidden together. A layer visible to only some people (a GM layer) hides all its markers from the others.";
    form.append(intro);
    const list = div("stack map-layers");
    for (const layer of map.layers) {
      const row = div("map-layer-row");
      const name = textInput("name", layer.name, "Layer name");
      let restrictedTo = layer.restrictedTo;
      const { element: who } = principalsInput({
        lookupUrl: `${this.#base}lookup`, me: this.#me(), value: layer.restrictedTo,
        placeholder: "everyone who sees the page", everyone: "Everyone who sees the page",
        onChange: (value) => (restrictedTo = value), onInput: (value) => (restrictedTo = value),
      });
      const noAi = document.createElement("input");
      noAi.type = "checkbox";
      noAi.checked = layer.noAi;
      const noAiLabel = document.createElement("label");
      noAiLabel.className = "check";
      noAiLabel.append(noAi, " Never show to AI");
      const save = button("Save", "", async () => {
        if (await this.#call("UpdateLayer", { layer: layer.id }, { name: name.value, restrictedTo, noAi: noAi.checked })) void this.#load();
      });
      const del = button("Delete", "Delete this layer (only when it has no markers)", async () => {
        if (await this.#call("DeleteLayer", { layer: layer.id }, {})) {
          await this.#load();
          this.#openLayers();
        }
      });
      del.className = "link danger";
      const count = map.markers.filter((m) => m.layerId === layer.id).length;
      const note = document.createElement("span");
      note.className = "muted small";
      note.textContent = `${count} marker${count === 1 ? "" : "s"}`;
      row.append(labelled("Name", name), labelled("Visible to", who), noAiLabel, note, save, del);
      list.append(row);
    }
    form.append(list);
    const actions = div("form-actions");
    actions.append(
      button("+ Layer", "Add a layer", async () => {
        if (await this.#call("AddLayer", {}, { name: `Layer ${map.layers.length + 1}` })) {
          await this.#load();
          this.#openLayers();
        }
      }),
      button("Close", "", () => this.#closePanel()),
    );
    form.append(actions);
  }

  #say(message: string): void {
    this.#status.textContent = message;
  }
}

// ---- Live: which map changed, from the page's live room (the map is then fetched through its own route) ----

type RecordListener = (kind: string, id: string) => void;
const listeners = new Set<RecordListener>();
let listening = false;

async function listen(): Promise<void> {
  if (listening) return;
  listening = true;
  try {
    const hub = await connection();
    hub.on("record", (r: { pageId: string; kind: string; id: string }) => listeners.forEach((l) => l(r.kind, r.id)));
  } catch {
    // Without the live connection, maps show what was there when the page was opened.
  }
}

// ---- Helpers ----

function markerIcon(icon: string): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 30");
  svg.classList.add("map-marker-icon");
  svg.innerHTML =
    '<path class="map-marker-pin" d="M12 29C6.5 21 2 16.5 2 11a10 10 0 0 1 20 0c0 5.5-4.5 10-10 18z"/>' +
    `<g class="map-marker-glyph" transform="translate(6.5 5.5) scale(1.1)">${GLYPHS[icon] ?? GLYPHS.pin}</g>`;
  return svg;
}

function iconChoices(icons: string[], custom: CustomIcon[], current: string): HTMLElement {
  const wrap = document.createElement("fieldset");
  wrap.className = "board-choices map-icon-choices";
  const legend = document.createElement("legend");
  legend.textContent = "Icon";
  const hidden = document.createElement("input");
  hidden.type = "hidden";
  hidden.name = "icon";
  hidden.value = current;
  wrap.append(legend, hidden);
  const add = (value: string, label: string, picture: Element): void => {
    const b = button("", label, () => {
      hidden.value = value;
      for (const other of wrap.querySelectorAll("button")) other.setAttribute("aria-pressed", String(other === b));
    });
    b.append(picture);
    b.setAttribute("aria-label", label);
    b.setAttribute("aria-pressed", String(value === current));
    wrap.append(b);
  };
  for (const icon of icons) add(icon, ICON_LABELS[icon] ?? icon, markerIcon(icon));
  for (const icon of custom) {
    const img = document.createElement("img");
    img.src = icon.url;
    img.alt = "";
    img.className = "map-icon-custom";
    add(icon.value, icon.name, img);
  }
  return wrap;
}

function escapeXml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

function grow(r: { left: number; top: number; right: number; bottom: number }, by: number) {
  const w = (r.right - r.left) * by;
  const h = (r.bottom - r.top) * by;
  return { left: r.left - w, top: r.top - h, right: r.right + w, bottom: r.bottom + h };
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

function token(): string {
  return (
    document.querySelector<HTMLMetaElement>('meta[name="request-token"]')?.content ??
    document.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]')?.value ??
    ""
  );
}

function div(className: string): HTMLDivElement {
  const el = document.createElement("div");
  el.className = className;
  return el;
}

function button(text: string, title: string, run: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.textContent = text;
  if (title) b.title = title;
  b.addEventListener("click", run);
  return b;
}

function textInput(name: string, value: string, placeholder: string): HTMLInputElement {
  const input = document.createElement("input");
  input.type = "text";
  input.name = name;
  input.value = value;
  input.placeholder = placeholder;
  return input;
}

function numberInput(name: string, value: number, title: string): HTMLInputElement {
  const input = document.createElement("input");
  input.type = "number";
  input.name = name;
  input.step = "any";
  input.value = String(Math.round(value * 100) / 100);
  input.title = title;
  return input;
}

function labelled(text: string, control: HTMLElement): HTMLElement {
  const wrap = document.createElement("label");
  wrap.className = "board-field";
  const caption = document.createElement("span");
  caption.textContent = text;
  wrap.append(caption, control);
  return wrap;
}

// A row of buttons that sets a hidden field (menus instead of <select>, as the owner prefers).
function choices(caption: string, name: string, values: string[], current: string, text: (v: string) => string): HTMLElement {
  const wrap = document.createElement("fieldset");
  wrap.className = `board-choices board-choices-${name}`;
  const legend = document.createElement("legend");
  legend.textContent = caption;
  const hidden = document.createElement("input");
  hidden.type = "hidden";
  hidden.name = name;
  hidden.value = current;
  wrap.append(legend, hidden);
  for (const value of values) {
    const b = button(text(value), text(value), () => {
      hidden.value = value;
      for (const other of wrap.querySelectorAll("button")) other.setAttribute("aria-pressed", String(other === b));
    });
    b.dataset.value = value;
    b.className = name === "color" ? `swatch color-${value}` : "";
    b.setAttribute("aria-pressed", String(value === current));
    wrap.append(b);
  }
  return wrap;
}

customElements.define("p9-map", P9Map);
